import { PrivateAPINamespace } from '../PrivateAPINamespace';
import { HostColorSelectorAPI, HostFontWeightSelectorAPI, HostLinkSelectorAPI, HostMediaSelectorAPI, } from './selectors';
export class BuilderComponentNamespace extends PrivateAPINamespace {
    async getStyleDefinitions(manifestContextId, childPath) {
        const resolver = this.ps.manifestsManager.getContextualManifestResolver(manifestContextId, childPath);
        return resolver.getStyleDefinitions();
    }
    async getStyles(manifestContextId, childPath) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.getStyles();
    }
    async setStyles(manifestContextId, childPath, values) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.setStyles(values);
    }
    async removeStyles(manifestContextId, childPath, values) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.removeStyles(values);
    }
    async getDataDefinitions(manifestContextId, childPath) {
        const resolver = this.ps.manifestsManager.getContextualManifestResolver(manifestContextId, childPath);
        return resolver.getDataDefinitions();
    }
    async getData(manifestContextId, childPath) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.getData();
    }
    async getResolvedData(manifestContextId, childPath, { dataItemKey }) {
        const resolver = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return resolver.getResolvedData({ dataItemKey });
    }
    async setData(manifestContextId, childPath, values) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.setData(values);
    }
    async getPresetDefinitions(manifestContextId, childPath) {
        const resolver = this.ps.manifestsManager.getContextualManifestResolver(manifestContextId, childPath);
        return resolver.getPresetDefinitions();
    }
    async getAppliedPreset(manifestContextId, childPath) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.getAppliedPreset();
    }
    async applyPreset(manifestContextId, childPath, options) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.applyPreset(options);
    }
    async getDisplayGroupDefinitions(manifestContextId, childPath, options) {
        const resolver = this.ps.manifestsManager.getContextualManifestResolver(manifestContextId, childPath);
        return resolver.getDisplayGroupDefinitions(options);
    }
    async onContextualManifestChange(_manifestContextId, cb) {
        return this.ps.manifestsManager.onContextualManifestChange(_manifestContextId, cb);
    }
    async selectMedia(manifestContextId, childPath, params) {
        const addBuilderType = (mediaObject) => {
            if (!mediaObject) {
                return undefined;
            }
            const typeMap = {
                video: 'Builder.Video',
                picture: 'Builder.Image',
                shape: 'Builder.VectorArt',
            };
            return mediaObject.map((media) => ({
                ...media,
                type: typeMap[media.mediaType],
            }));
        };
        if ('dataItemKey' in params) {
            const hostMediaSelectorAPI = new HostMediaSelectorAPI(this.ps);
            const [dataDefinitions, data] = await Promise.all([
                this.getDataDefinitions(manifestContextId, childPath),
                this.getData(manifestContextId, childPath),
            ]);
            const media = await hostMediaSelectorAPI.selectMedia(params.dataItemKey, {
                dataDefinitions,
                data,
            });
            if (media) {
                await this.setData(manifestContextId, childPath, {
                    [params.dataItemKey]: addBuilderType(media),
                });
                await this.waitForChangesApplied();
                const updatedData = await this.getData(manifestContextId, childPath);
                return updatedData?.[params.dataItemKey];
            }
            return undefined;
        }
        else {
            return addBuilderType(await this.ps.hostAPI.editor.selectMedia(params));
        }
    }
    async selectLink(manifestContextId, childPath, params) {
        if ('dataItemKey' in params) {
            const hostLinkSelectorAPI = new HostLinkSelectorAPI(this.ps);
            const [dataDefinitions, data] = await Promise.all([
                this.getDataDefinitions(manifestContextId, childPath),
                this.getData(manifestContextId, childPath),
            ]);
            const link = await hostLinkSelectorAPI.selectLink(params.dataItemKey, {
                dataDefinitions,
                data,
            });
            if (link) {
                await this.setData(manifestContextId, childPath, {
                    [params.dataItemKey]: link,
                });
                await this.waitForChangesApplied();
                const updatedData = await this.getData(manifestContextId, childPath);
                return updatedData?.[params.dataItemKey];
            }
            return undefined;
        }
        else {
            return this.ps.hostAPI.editor.selectLink(params.value, params.options);
        }
    }
    /**
     * TODO: support both cssProperties & cssCustomProperties
     */
    async selectColor(manifestContextId, childPath, params) {
        if ('styleItemKey' in params) {
            const styleItemKey = params.styleItemKey;
            const [styleDefinitions, styles] = await Promise.all([
                this.getStyleDefinitions(manifestContextId, childPath),
                this.getStyles(manifestContextId, childPath),
            ]);
            await new HostColorSelectorAPI(this.ps).selectColor(params.styleItemKey, {
                styleDefinitions: styleDefinitions.cssProperties,
                styles: styles.cssProperties,
                onChange: (color) => {
                    if (styleItemKey) {
                        void this.setStyles(manifestContextId, childPath, {
                            [styleItemKey]: color,
                        });
                    }
                },
            });
            await this.waitForChangesApplied();
            const updatedStyles = await this.getStyles(manifestContextId, childPath);
            return (updatedStyles?.cssProperties?.[styleItemKey] ??
                updatedStyles?.cssCustomProperties?.[styleItemKey]);
        }
        else {
            return this.ps.hostAPI.editor.selectColor(params);
        }
    }
    /**
     * TODO: support both cssProperties & cssCustomProperties
     */
    async selectFont(manifestContextId, childPath, params) {
        if ('fontStyleItemKey' in params) {
            const hostFontWeightSelectorAPI = new HostFontWeightSelectorAPI(this.ps);
            const [styleDefinitions, styles] = await Promise.all([
                this.getStyleDefinitions(manifestContextId, childPath),
                this.getStyles(manifestContextId, childPath),
            ]);
            const font = await hostFontWeightSelectorAPI.selectFont({
                fontStyleItemKey: params.fontStyleItemKey,
            }, {
                // TODO: we need to call setStyles after font is selected
                onChange: params.onChange,
                styleDefinitions: styleDefinitions.cssProperties,
                // @ts-expect-error TODO: uncomment as soon as we add types for getStyles function
                styles: styles.cssProperties,
            });
            if (font) {
                const key = params.fontStyleItemKey;
                await this.setStyles(manifestContextId, childPath, {
                    [key]: font,
                });
                await this.waitForChangesApplied();
                const updatedStyles = await this.getStyles(manifestContextId, childPath);
                return (updatedStyles?.cssProperties?.[key] ??
                    updatedStyles?.cssCustomProperties?.[key]);
            }
            return undefined;
        }
        else {
            return this.ps.hostAPI.editor.selectFontFamily(params);
        }
    }
    async selectFontWeight(manifestContextId, childPath, params) {
        if (('fontFamilyStyleItemKey' in params &&
            'fontWeightStyleItemKey' in params) ||
            'fontStyleItemKey' in params) {
            const hostFontWeightSelectorAPI = new HostFontWeightSelectorAPI(this.ps);
            const [styleDefinitions, styles] = await Promise.all([
                this.getStyleDefinitions(manifestContextId, childPath),
                this.getStyles(manifestContextId, childPath),
            ]);
            const font = await hostFontWeightSelectorAPI.selectFontWeight({
                fontFamilyStyleItemKey: params.fontFamilyStyleItemKey,
                fontWeightStyleItemKey: params.fontWeightStyleItemKey,
                fontStyleItemKey: params.fontStyleItemKey,
            }, {
                // TODO: we need to call setStyles after font weight is selected
                onChange: params.onChange,
                styleDefinitions: styleDefinitions.cssProperties,
                // @ts-expect-error TODO: uncomment as soon as we add types for getStyles function
                styles: styles.cssProperties,
            });
            if (font) {
                const key = (params.fontWeightStyleItemKey ?? params.fontStyleItemKey);
                const value = params.fontWeightStyleItemKey ? font.weight : font;
                await this.setStyles(manifestContextId, childPath, {
                    [key]: value,
                });
                await this.waitForChangesApplied();
                const updatedStyles = await this.getStyles(manifestContextId, childPath);
                return (updatedStyles?.cssProperties?.[key] ??
                    updatedStyles?.cssCustomProperties?.[key]);
            }
            return undefined;
        }
        else {
            return this.ps.hostAPI.editor.selectFontWeight(params);
        }
    }
    async selectTextTheme(_manifestContextId, // TODO: EP-6616 implement connected mode
    params) {
        return this.ps.hostAPI.editor.selectTextTheme(params);
    }
    async getState(manifestContextId, childPath) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.getState();
    }
    async setState(manifestContextId, childPath, state) {
        const query = this.ps.manifestsManager.getContextualManifestQuery(manifestContextId, childPath);
        return query.setState(state);
    }
    async getStateDefinitions(manifestContextId, childPath) {
        const resolver = this.ps.manifestsManager.getContextualManifestResolver(manifestContextId, childPath);
        return resolver.getStateDefinitions();
    }
}
BuilderComponentNamespace.namespace = 'builderComponent';
//# sourceMappingURL=BuilderComponentNamespace.js.map